import { ComponentClassNames } from '../../util/classNames';
import { Depth, getDepthBoxShadow } from '../../util/styles/depth';
import { ZIndex } from '../../util/styles/zIndex';
import { ITheme, memoizeFunction, mergeStyleSets } from '../../util/styling';

import { PageLayoutWidth } from './PageLayout.types';

const heroBannerContentWrapperGradientTopMarginNarrow = 24;
const heroBannerContentWrapperGradientTopMarginWide = 68;

export const leftSidebarLeftMargin = 26;
export const minLeftSidebarWidth = 149;
export const maxLeftSidebarWidth = 249;

export const maxCenterColumnWidth = 680;
export const centerColumnVerticalGutter = 12;

export const contentSidebarWidthNarrow = 292;
export const contentSidebarWidthWide = 320;

export const horizontalMargin = 10;
const contentSidebarPadding = 16;

export const minLeftSidebarRightMargin = 26 - horizontalMargin;
export const rightContentSidebarMinLeftMargin = 26 - horizontalMargin - contentSidebarPadding;
export const rightContentSidebarMaxLeftMargin = 48 - horizontalMargin - contentSidebarPadding;
export const rightContentSidebarRightMargin = 25 - contentSidebarPadding;

export const leftContentSidebarMinLeftMargin = rightContentSidebarMinLeftMargin + 10;
export const leftContentSidebarMaxLeftMargin = rightContentSidebarMaxLeftMargin - 12;

export const leftContentSidebarMinRightMargin = rightContentSidebarRightMargin - 9;
export const leftContentSidebarMaxRightMargin = rightContentSidebarRightMargin + 13;

// Content = CenterColumn + ContentSidebar
export const defaultHeaderHeight = 48;
export const headerHeightSmall = 44;
export const defaultSubHeaderHeight = 42;
export const defaultBannerHeight = 32;
export const defaultFooterHeight = 38;
export const verticalGutterHeight = 24;
export const defaultHeaderHeightWithSpacing = defaultHeaderHeight + verticalGutterHeight;

export const buttonLength = 44;

export const noScrollClass = 'noScroll';

// calculating leftSidebar and contentColumn width (when sidebar is collapsed) percentages
const leftSidebarWithMargins = leftSidebarLeftMargin + maxLeftSidebarWidth + minLeftSidebarRightMargin;
const contentColumnWithMargins =
  maxCenterColumnWidth + 2 * horizontalMargin + contentSidebarWidthNarrow + rightContentSidebarRightMargin;
export const totalWidth = leftSidebarWithMargins + contentColumnWithMargins;
export const contentColumnWidthPercent = 100 * (contentColumnWithMargins / totalWidth);

export const getContentColumnWidth = (width: PageLayoutWidth | undefined) =>
  width === PageLayoutWidth.Small ? '100%' : `${contentColumnWidthPercent}%`;

export const getMaxContentColumnWidth = (showLeftSidebar: boolean, width?: PageLayoutWidth) => {
  const availableLeftSidebarWidth = showLeftSidebar ? 0 : leftSidebarWithMargins;
  const maxContentWidth =
    availableLeftSidebarWidth +
    maxCenterColumnWidth +
    horizontalMargin * 2 +
    contentSidebarWidthNarrow +
    rightContentSidebarMaxLeftMargin +
    rightContentSidebarRightMargin;

  switch (width) {
    case PageLayoutWidth.Large:
      return maxContentWidth + rightContentSidebarMinLeftMargin - rightContentSidebarMaxLeftMargin;
    case PageLayoutWidth.XLarge:
      return maxContentWidth;
    default:
      return contentColumnWithMargins;
  }
};

const getFullWidthBoxShadow = (fullWidthLayout: boolean | undefined, theme: ITheme, showLeftSidebar: boolean) => {
  if (!fullWidthLayout || theme.isInverted || !showLeftSidebar) {
    return undefined;
  }

  return getDepthBoxShadow(Depth.depth6);
};

interface GetClassNamesProps {
  readonly contentTopOffset: number;
  readonly fullWidthLayout?: boolean;
  readonly noSpacing: boolean;
  readonly supressNoScrollRightPadding: boolean;
  readonly showLeftSidebar: boolean;
  readonly hasHeroBanner: boolean;
  readonly theme: ITheme;
  readonly width?: PageLayoutWidth;
}

type PageLayoutClassNames = ComponentClassNames<
  'pageLayout' | 'contentWrapper' | 'collapsiblePanelContainer' | 'contentColumn'
>;

export const getClassNames = memoizeFunction(
  ({
    contentTopOffset,
    fullWidthLayout,
    hasHeroBanner,
    noSpacing,
    supressNoScrollRightPadding,
    showLeftSidebar,
    theme,
    width,
  }: GetClassNamesProps): PageLayoutClassNames => {
    const isSmall = width === PageLayoutWidth.Small;
    const contentWrapperTopMarginByWidth = isSmall
      ? heroBannerContentWrapperGradientTopMarginNarrow
      : heroBannerContentWrapperGradientTopMarginWide;
    const contentWrapperTopMargin = hasHeroBanner ? contentWrapperTopMarginByWidth : undefined;
    const contentColumnWidth = getContentColumnWidth(width);
    const maxContentColumnWidth = noSpacing ? undefined : getMaxContentColumnWidth(showLeftSidebar, width);

    const scrollbarWidth = supressNoScrollRightPadding
      ? 0
      : Math.max(0, window.innerWidth - document.documentElement.clientWidth);

    return mergeStyleSets({
      pageLayout: {
        display: 'flex',
        justifyContent: 'center',
        // Only this container can have a min-height, IE11 will improperly calculate child sizes if a child
        // has min-height but no height value.
        // https://caniuse.com/#feat=flexbox
        minHeight: '100vh',
        selectors: {
          ':global(body)': {
            overflowY: 'scroll',
          },
          [`:global(body.${noScrollClass})`]: {
            overflowY: 'hidden',
            paddingRight: scrollbarWidth,
          },
        },
      },
      collapsiblePanelContainer: {
        position: 'fixed',
        top: contentTopOffset,
        right: 0,
        bottom: 0,
        zIndex: ZIndex.PageLayoutCollapsiblePanel,
      },
      contentWrapper: {
        display: 'flex',
        marginTop: contentWrapperTopMargin,
        justifyContent: fullWidthLayout ? undefined : 'center',
        width: '100%',
      },
      contentColumn: {
        flex: '1 1 auto',
        width: contentColumnWidth,
        maxWidth: fullWidthLayout ? undefined : maxContentColumnWidth,
        zIndex: ZIndex.PageLayoutContentColumn,
        boxShadow: getFullWidthBoxShadow(fullWidthLayout, theme, showLeftSidebar),
      },
    });
  }
);
